import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import './AuthModal.css';

const AuthModal: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { login, createRandomUser, isAuthenticated } = useAuth();
  
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/home');
    }
  }, [isAuthenticated, navigate]);

  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
        navigate('/home');
      } else {
        if (password !== confirmPassword) {
          throw new Error(t('passwordsDoNotMatch') as string);
        }
        await login(email, password);
        navigate('/home');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('authError') as string);
    } finally {
      setLoading(false);
    }
  };

  const handleGuestLogin = async () => {
    try {
      setLoading(true);
      await createRandomUser();
      navigate('/home');
    } catch (error) {
      console.error('Guest login error:', error);
      setError(t('guestLoginError') as string);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-modal">
      <div className="auth-modal-content">
        <h2>{isLogin ? t('login') : t('register')}</h2>
        <p className="auth-modal-description">
          {isLogin ? t('loginDescription') : t('registerDescription')}
        </p>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">{t('email')}</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder={t('enterEmail') as string}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('password')}</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder={t('enterPassword') as string}
            />
          </div>

          {!isLogin && (
            <div className="form-group">
              <label htmlFor="confirmPassword">{t('confirmPassword')}</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder={t('confirmPassword') as string}
              />
            </div>
          )}

          {error && <div className="error-message">{error}</div>}

          <button type="submit" className="auth-button submit-button" disabled={loading}>
            {loading ? t('loading') : (isLogin ? t('login') : t('register'))}
          </button>
        </form>

        <div className="auth-options">
          <button 
            type="button"
            className="auth-button switch-mode-button"
            onClick={() => setIsLogin(!isLogin)}
          >
            {isLogin ? t('needAccount') : t('haveAccount')}
          </button>

          <button 
            type="button"
            className="auth-button guest-button"
            onClick={handleGuestLogin}
            disabled={loading}
          >
            {t('continueAsGuest')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthModal; 